import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Card, CardHeader, CardContent, IconButton, Box, Typography, Button, Stack, Paper, Divider, Tooltip, Skeleton, Chip } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SavingsIcon from '@mui/icons-material/Savings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import { useThemeContext } from '../../context/ThemeContext';
import KpiCard from './KpiCard';
import SpendingPieChart from './SpendingPieChart';
import IncomeSpendingLineChart from './IncomeSpendingLineChart';
import AccountBalancesList from './AccountBalancesList';
import AddAccountDialog from './AddAccountDialog';
import AddTransactionDialog from './AddTransactionDialog';

const TrackerDashboard = () => {
    const { mode, toggleTheme } = useThemeContext();

    const [editingAccount, setEditingAccount] = useState(null);
    const [openAccountDialog, setOpenAccountDialog] = useState(false);
    const [openTransactionDialog, setOpenTransactionDialog] = useState(false);
    const [transactionTypePreset, setTransactionTypePreset] = useState('expense');
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStartDate, setFilterStartDate] = useState(null);
    const [filterEndDate, setFilterEndDate] = useState(null);
    const [largestTransaction, setLargestTransaction] = useState(null);
    const [insightsLoading, setInsightsLoading] = useState(true);
    const [insightPeriodLabel, setInsightPeriodLabel] = useState(dayjs().format('MMM YYYY'));

    // --- NEW: State for Net Worth Visibility, persisted with localStorage ---
    const [isNetWorthVisible, setIsNetWorthVisible] = useState(() => {
        try {
            const savedState = localStorage.getItem('isNetWorthVisible');
            // Return true if nothing is saved, otherwise parse the saved string
            return savedState !== null ? JSON.parse(savedState) : true;
        } catch (error) {
            // If parsing fails, default to true
            return true;
        }
    });

    // --- NEW: More robust initial state to prevent 'undefined' errors ---
    const [summaryData, setSummaryData] = useState({
        net_worth: { total_in_php: 0, php_total: 0, usd_total: 0 },
        income_this_month: { total_in_php: 0, php_total: 0, usd_total: 0 },
        spending_this_month: { total_in_php: 0, php_total: 0, usd_total: 0 },
        income_last_month: { total_in_php: 0 },
        spending_last_month: { total_in_php: 0 },
        spending_by_category: [],
        monthly_summary: [],
    });
    const netIncome = summaryData.income_this_month.total_in_php - summaryData.spending_this_month.total_in_php;
    // Determine the color based on the value
    const netIncomeColor = netIncome >= 0 ? 'success.main' : 'error.main';

    // --- NEW: Calculate Net Income for Last Month ---
    const lastMonthNetIncome = summaryData.income_last_month.total_in_php - summaryData.spending_last_month.total_in_php;
    const lastMonthNetIncomeColor = lastMonthNetIncome >= 0 ? 'success.main' : 'error.main';
    const phpSymbol = '₱';
    const totalAccounts = accounts.length;
    const largestAccount = accounts.reduce((max, account) => {
        if (!max) return account;
        return account.balance > max.balance ? account : max;
    }, null);
    const totalsByCurrency = accounts.reduce(
        (acc, account) => {
            if (account.currency === 'USD') acc.usd += account.balance;
            else acc.php += account.balance;
            return acc;
        },
        { php: 0, usd: 0 }
    );
    const topCategory = [...summaryData.spending_by_category]
        .sort((a, b) => (b?.value || 0) - (a?.value || 0))[0];

    const fetchInsights = useCallback(() => {
        const periodSource = filterStartDate || dayjs();
        const year = periodSource.year();
        const month = periodSource.month() + 1;
        setInsightPeriodLabel(periodSource.format('MMM YYYY'));
        setInsightsLoading(true);

        fetch(`https://frek.cc/proxy/38.54.86.24:8001/api/transactions?year=${year}&month=${month}`)
            .then(res => res.json())
            .then((data) => {
                const transactions = Array.isArray(data) ? data : [];
                const biggest = transactions.reduce((currentMax, transaction) => {
                    const amount = Math.abs(Number(transaction.amount) || 0);
                    if (!currentMax) return transaction;
                    const currentAmount = Math.abs(Number(currentMax.amount) || 0);
                    return amount > currentAmount ? transaction : currentMax;
                }, null);
                setLargestTransaction(biggest);
                setInsightsLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching transactions for insights:', error);
                setLargestTransaction(null);
                setInsightsLoading(false);
            });
    }, [filterStartDate]);

    // --- NEW: Persist visibility state to localStorage on change ---
    useEffect(() => {
        localStorage.setItem('isNetWorthVisible', JSON.stringify(isNetWorthVisible));
    }, [isNetWorthVisible]);

    const fetchData = useCallback((start, end) => {
        setLoading(true);
        let summaryUrl = 'https://frek.cc/proxy/38.54.86.24:8001/api/dashboard-summary';

        if (start && end) {
            const startDateStr = start.format('YYYY-MM-DD');
            const endDateStr = end.format('YYYY-MM-DD');
            summaryUrl += `?start_date=${startDateStr}&end_date=${endDateStr}`;
        }

        const accountsPromise = fetch('https://frek.cc/proxy/38.54.86.24:8001/api/accounts').then(res => res.json());
        const summaryPromise = fetch(summaryUrl).then(res => res.json());

        Promise.all([accountsPromise, summaryPromise])
            .then(([accountsData, summaryData]) => {
                setAccounts(accountsData);
                // Ensure summaryData has default values if the API response is partial
                setSummaryData(currentData => ({ ...currentData, ...summaryData }));
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching data:", error);
                setLoading(false);
            });
    }, []); // useCallback with empty dependency array

    useEffect(() => {
        fetchData(filterStartDate, filterEndDate);
    }, [filterStartDate, filterEndDate, fetchData]);

    useEffect(() => {
        fetchInsights();
    }, [fetchInsights]);

    // --- NEW: Handler to toggle visibility state ---
    const toggleNetWorthVisibility = () => {
        setIsNetWorthVisible(prevState => !prevState);
    };

    const handleFilter = () => fetchData(filterStartDate, filterEndDate);
    const handleClearFilter = () => {
        setFilterStartDate(null);
        setFilterEndDate(null);
    };

    const handleOpenAddAccountDialog = () => { setEditingAccount(null); setOpenAccountDialog(true); };
    const handleOpenEditDialog = (account) => { setEditingAccount(account); setOpenAccountDialog(true); };
    const handleCloseAccountDialog = () => { setOpenAccountDialog(false); setEditingAccount(null); };
    const handleSuccess = () => {
        handleCloseAccountDialog();
        setOpenTransactionDialog(false);
        fetchData(filterStartDate, filterEndDate);
        fetchInsights();
    };

    const handleQuickTransaction = (type) => {
        setTransactionTypePreset(type);
        setOpenTransactionDialog(true);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Stack spacing={2} sx={{ mb: 3 }}>
                    <Paper sx={{ p: 2.5, borderRadius: 3 }}>
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }} justifyContent="space-between">
                            <Box>
                                <Typography variant="h4" component="h1">Portfolio Overview</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Track cash flow, balances, and net performance across accounts.
                                </Typography>
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
                                    <IconButton onClick={toggleTheme} color="inherit">
                                        {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
                                    </IconButton>
                                </Tooltip>
                                <Button variant="contained" color="success" onClick={() => handleQuickTransaction('income')} startIcon={<TrendingUpIcon />}>
                                    Add Income
                                </Button>
                                <Button variant="contained" color="error" onClick={() => handleQuickTransaction('expense')} startIcon={<TrendingDownIcon />}>
                                    Add Expense
                                </Button>
                                <Button variant="outlined" onClick={handleOpenAddAccountDialog} startIcon={<AccountBalanceIcon />}>
                                    Add Account
                                </Button>
                            </Stack>
                        </Stack>
                    </Paper>
                    <Paper sx={{ p: 2, borderRadius: 3 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} flexWrap="wrap">
                                <DatePicker label="Start Date" value={filterStartDate} onChange={setFilterStartDate} slotProps={{ textField: { size: 'small' } }} />
                                <DatePicker label="End Date" value={filterEndDate} onChange={setFilterEndDate} slotProps={{ textField: { size: 'small' } }} />
                                <Button variant="outlined" onClick={handleFilter} size="small">Apply Filter</Button>
                                <Button onClick={handleClearFilter} size="small">Clear</Button>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                    Updated {dayjs().format('MMM D, YYYY')}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={12}>
                        {/* --- MODIFIED: Added visibility toggle and conditional rendering --- */}
                        <KpiCard
                            title={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    Total Net Worth
                                    <IconButton onClick={toggleNetWorthVisibility} size="small" sx={{ ml: 1 }} title={isNetWorthVisible ? 'Hide Net Worth' : 'Show Net Worth'}>
                                        {isNetWorthVisible ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </Box>
                            }
                            value={isNetWorthVisible ? `${phpSymbol}${summaryData.net_worth.total_in_php.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '********'}
                            subtitle={isNetWorthVisible ? `(${phpSymbol}${summaryData.net_worth.php_total.toLocaleString()} + $${summaryData.net_worth.usd_total.toLocaleString()})` : '(Hidden)'}
                            icon={<AccountBalanceIcon fontSize="small" />}
                            accent="primary.main"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <KpiCard
                            title="Income (Selected Period)"
                            value={`${phpSymbol}${summaryData.income_this_month.total_in_php.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            subtitle={`(${phpSymbol}${summaryData.income_this_month.php_total.toLocaleString()} + $${summaryData.income_this_month.usd_total.toLocaleString()})`}
                            icon={<TrendingUpIcon fontSize="small" />}
                            accent="success.main"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <KpiCard
                            title="Spending (Selected Period)"
                            value={`${phpSymbol}${summaryData.spending_this_month.total_in_php.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            subtitle={`(${phpSymbol}${summaryData.spending_this_month.php_total.toLocaleString()} + $${summaryData.spending_this_month.usd_total.toLocaleString()})`}
                            icon={<TrendingDownIcon fontSize="small" />}
                            accent="error.main"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <KpiCard
                            title="Net Income (Selected Period)"
                            value={`${netIncome >= 0 ? '+' : ''}${phpSymbol}${netIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            subtitle="Income - Spending"
                            valueColor={netIncomeColor} // Pass the dynamic color
                            icon={<ShowChartIcon fontSize="small" />}
                            accent={netIncome >= 0 ? 'success.main' : 'error.main'}
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <KpiCard
                            title="Last Month's Income"
                            value={`${phpSymbol}${summaryData.income_last_month.total_in_php.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            subtitle="Previous calendar month"
                            icon={<SavingsIcon fontSize="small" />}
                            accent="success.main"
                            loading={loading}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <KpiCard
                            title="Last Month's Spending"
                            value={`${phpSymbol}${summaryData.spending_last_month.total_in_php.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            subtitle="Previous calendar month"
                            icon={<ReceiptLongIcon fontSize="small" />}
                            accent="warning.main"
                            loading={loading}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                        <KpiCard
                            title="Last Month's Net Income"
                            value={`${lastMonthNetIncome >= 0 ? '+' : ''}${phpSymbol}${lastMonthNetIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                            subtitle="Previous calendar month"
                            valueColor={lastMonthNetIncomeColor}
                            icon={<PriceCheckIcon fontSize="small" />}
                            accent={lastMonthNetIncome >= 0 ? 'success.main' : 'error.main'}
                            loading={loading}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3 }}>
                            <CardHeader title="Mini Insights" subheader="Fast highlights from your data" />
                            <Divider sx={{ mb: 2 }} />
                            <CardContent sx={{ display: 'grid', gap: 2 }}>
                                {loading || insightsLoading ? (
                                    <Stack spacing={2}>
                                        <Skeleton variant="rectangular" height={72} />
                                        <Skeleton variant="rectangular" height={72} />
                                        <Skeleton variant="rectangular" height={72} />
                                    </Stack>
                                ) : (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 2,
                                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                        }}
                                    >
                                        <Paper sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Top Spending Category</Typography>
                                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                                                {topCategory?.name || 'No category data'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {topCategory ? `${phpSymbol}${Number(topCategory.value).toLocaleString()} this period` : 'Add expenses to see trends.'}
                                            </Typography>
                                        </Paper>
                                        <Paper sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Largest Transaction</Typography>
                                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                                                {largestTransaction ? largestTransaction.description || 'Untitled' : 'No transactions yet'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {largestTransaction
                                                    ? `${largestTransaction.type === 'income' ? '+' : '-'}${largestTransaction.currency === 'USD' ? '$' : phpSymbol}${Number(largestTransaction.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                    : 'Add transactions to see this insight.'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Period: {insightPeriodLabel}
                                            </Typography>
                                        </Paper>
                                        <Paper sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Largest Account</Typography>
                                            <Typography variant="h6" sx={{ mt: 0.5 }}>
                                                {largestAccount ? largestAccount.name : 'No accounts yet'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {largestAccount
                                                    ? `${largestAccount.currency === 'USD' ? '$' : phpSymbol}${largestAccount.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                    : 'Create an account to track balances.'}
                                            </Typography>
                                        </Paper>
                                        <Paper sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Liquidity Snapshot</Typography>
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                                                <Chip label={`${totalAccounts} accounts`} color="primary" variant="outlined" />
                                                <Chip label={`${phpSymbol}${totalsByCurrency.php.toLocaleString()}`} variant="outlined" />
                                                <Chip label={`$${totalsByCurrency.usd.toLocaleString()}`} variant="outlined" />
                                            </Stack>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                Combined balances across currencies.
                                            </Typography>
                                        </Paper>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card sx={{ height: '400px', p: 2, borderRadius: 3 }}>
                            <CardHeader
                                title="Income vs. Spending (Last 6 Months)"
                                subheader="Momentum and directional trend"
                            />
                            <Divider sx={{ mb: 2 }} />
                            <CardContent sx={{ height: '100%', mt: -1 }}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={300} />
                                ) : (
                                    <Box sx={{ height: '100%', animation: 'riseFade 600ms ease both' }}>
                                        <IncomeSpendingLineChart data={summaryData.monthly_summary} />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '500px', pt: 1, borderRadius: 3 }}>
                            <CardHeader title="Spending by Category" subheader="Composition of expenses" />
                            <Divider sx={{ mb: 2 }} />
                            <CardContent sx={{ height: '100%', mt: -3 }}>
                                {loading ? (
                                    <Skeleton variant="rectangular" height={360} />
                                ) : (
                                    <Box sx={{ height: '100%', animation: 'riseFade 600ms ease both' }}>
                                        <SpendingPieChart data={summaryData.spending_by_category} />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ maxHeight: '500px', overflow: 'auto', borderRadius: 3 }}>
                            <CardHeader
                                title="Account Balances"
                                subheader="Liquidity across accounts"
                            />
                            <Divider sx={{ mb: 1 }} />
                            <CardContent sx={{ mt: -1, px: 1 }}>
                                {loading ? <Skeleton variant="rectangular" height={320} /> : (
                                    <AccountBalancesList
                                        accounts={accounts}
                                        loading={loading}
                                        onEdit={handleOpenEditDialog}
                                        onAccountChange={() => fetchData(filterStartDate, filterEndDate)}
                                    />
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <AddAccountDialog open={openAccountDialog} onClose={handleCloseAccountDialog} onSuccess={handleSuccess} accountToEdit={editingAccount} />
                <AddTransactionDialog open={openTransactionDialog} onClose={() => setOpenTransactionDialog(false)} onSuccess={handleSuccess} accounts={accounts} defaultType={transactionTypePreset} />
            </Box>
        </LocalizationProvider>
    );
};

export default TrackerDashboard;


